// Lightweight bilingual (JA/EN) layer scoped to the Meta-review pages.
//
// The app has no i18n framework, and pulling one in for two pages is overkill.
// Instead we persist the chosen language in localStorage and broadcast changes
// via a window CustomEvent so every subscriber (both pages + the sidebar links)
// re-renders instantly when the language is toggled.
//
// Default language is derived from navigator.language: a Meta reviewer on an
// English locale sees English out of the box, while Japanese users keep 日本語.
import { useState, useEffect, useCallback } from 'react';

export type MetaLang = 'ja' | 'en';

const STORAGE_KEY = 'meta_review_lang';
const CHANGE_EVENT = 'meta-review-lang-change';
const JAPANESE_TEXT_PATTERN = /[\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Han}]/u;

/** True when user/API-provided display text contains Japanese characters. */
export function containsJapaneseText(value: string): boolean {
  return JAPANESE_TEXT_PATTERN.test(value);
}

/**
 * Keep persisted data untouched, but prevent Japanese dynamic text from
 * leaking into an English Meta App Review recording.
 */
export function reviewSafeText(
  value: string | null | undefined,
  lang: MetaLang,
  fallback: string,
): string {
  const normalized = value?.trim();
  if (!normalized) return fallback;
  return lang === 'en' && containsJapaneseText(normalized) ? fallback : normalized;
}

export function getMetaLang(): MetaLang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'ja' || stored === 'en') return stored;
  } catch {
    /* localStorage unavailable */
  }
  const nav = (typeof navigator !== 'undefined' && navigator.language) || 'en';
  return nav.toLowerCase().startsWith('ja') ? 'ja' : 'en';
}

export function setMetaLang(lang: MetaLang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: lang }));
  }
}

/** React hook: returns [lang, setLang]; re-renders on toggle (this tab + others). */
export function useMetaLang(): [MetaLang, (lang: MetaLang) => void] {
  const [lang, setLang] = useState<MetaLang>(getMetaLang());

  useEffect(() => {
    const sync = () => setLang(getMetaLang());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const update = useCallback((l: MetaLang) => setMetaLang(l), []);
  return [lang, update];
}

// ---- Dictionaries -------------------------------------------------------

type Dict<T> = Record<MetaLang, T>;

export const TRIAL_BANNER_STRINGS: Dict<{
  remaining: (days: number) => string;
  endingSoon: string;
  upgrade: string;
  close: string;
}> = {
  ja: {
    remaining: (days: number) => `トライアル残り ${days} 日`,
    endingSoon: 'まもなく終了します',
    upgrade: 'プランを選択してアップグレード',
    close: 'バナーを閉じる',
  },
  en: {
    remaining: (days: number) => `Trial: ${days} days left`,
    endingSoon: 'Ending soon',
    upgrade: 'Choose a plan to upgrade',
    close: 'Close banner',
  },
};

export const MC_STRINGS: Dict<{
  pageTitle: string;
  loading: string;
  providerName: string;
  providerMetaSuffix: string; // appended after the API version
  connected: string;
  notConnected: string;
  account: string;
  adAccounts: string;
  permissions: string;
  openDashboard: string;
  disconnect: string;
  processing: string;
  connect: string;
  connecting: string;
  tokenLabel: string;
  tokenPlaceholder: string;
  helpTitle: string;
  helpBody: string;
  errEmpty: string;
  errConnect: string;
  errDisconnect: string;
  success: string;
  // --- Customer-self OAuth (ads_read + ads_management) ---
  oauthSectionTitle: string;
  oauthSectionBody: string;
  oauthConnect: string;
  oauthRedirecting: string;
  oauthScopesLabel: string;
  oauthNoBusinessManagement: string;
  pickerTitle: string;
  pickerBody: string;
  pickerConfirm: string;
  pickerConfirming: string;
  pickerCancel: string;
  pickerEmpty: string;
  pickerNoSelection: string;
  errOAuthStart: string;
  errOAuthCandidates: string;
  errOAuthConfirm: string;
  oauthCancelled: string;
  oauthErrors: Record<string, string>;
  connectSuccess: string;
  // --- Write capability / re-authorization ---
  writeEnabled: string;
  writeDisabled: string;
  reauthTitle: string;
  reauthBody: string;
  reauthCta: string;
  lastChecked: string;
  testAccount: string;
  authMethod: string;
  authMethodOAuth: string;
  authMethodToken: string;
  advancedTokenToggleShow: string;
  advancedTokenToggleHide: string;
  // --- Multiple ad accounts (agency) ---
  addAccount: string;
  disconnectAll: string;
  disconnectOne: string;
  confirmDisconnectOne: string;
  disconnectOneSuccess: string;
  connectedBy: string;
}> = {
  ja: {
    pageTitle: 'メディア連携設定',
    loading: '読み込み中...',
    providerName: 'Meta 広告',
    providerMetaSuffix: ' ・ ads_read + ads_management',
    connected: '接続済み',
    notConnected: '未接続',
    account: '接続アカウント',
    adAccounts: '広告アカウント',
    permissions: '付与された権限',
    openDashboard: 'ダッシュボードを開く',
    disconnect: '切断する',
    processing: '処理中...',
    connect: '接続する',
    connecting: '接続中...',
    tokenLabel: 'System User Token',
    tokenPlaceholder: 'EAAB... （Business Manager で発行した System User Token を貼り付け）',
    helpTitle: 'System User Token について',
    helpBody:
      '管理者向けの代替手段です。通常は上の「Meta 広告を連携」（OAuth）をご利用ください。Meta ビジネスマネージャの「ビジネス設定 > システムユーザー」で発行したトークンを貼り付けると、サーバー間（server-to-server）で検証して接続します。トークンは暗号化して保存されます。閲覧のみなら ads_read、作成・編集・停止も行う場合は ads_management も必要です。接続時にバックエンドが /me・/me/permissions・/me/adaccounts を呼び出して検証します。',
    errEmpty: 'System User Token を入力してください。',
    errConnect: '接続に失敗しました。System User Token が正しいか、有効期限が切れていないかご確認ください。',
    errDisconnect: '切断に失敗しました。',
    success: '接続に成功しました。',
    oauthSectionTitle: 'Meta 広告を連携',
    oauthSectionBody:
      'ご自身の Meta アカウントで認可すると、広告実績の取得と、確認画面での承認を経た広告の作成・編集・停止が利用できます。トークンは暗号化して保存され、画面・ログ・API レスポンスには表示されません。',
    oauthConnect: 'Meta 広告を連携',
    oauthRedirecting: 'Meta に移動しています...',
    oauthScopesLabel: '要求する権限',
    oauthNoBusinessManagement: 'business_management は要求しません（Business 資産の管理は行いません）。',
    pickerTitle: '利用する広告アカウントを選択',
    pickerBody: 'ログインした Meta ユーザーがアクセスできる広告アカウントのみ表示しています。',
    pickerConfirm: '選択したアカウントを接続',
    pickerConfirming: '接続中...',
    pickerCancel: 'キャンセル',
    pickerEmpty: 'アクセス可能な広告アカウントが見つかりませんでした。',
    pickerNoSelection: '広告アカウントを1つ以上選択してください。',
    errOAuthStart: 'Meta 認可を開始できませんでした。',
    errOAuthCandidates: '認証セッションが見つかりません。もう一度連携をやり直してください。',
    errOAuthConfirm: '広告アカウントの接続に失敗しました。',
    oauthCancelled: 'Meta 認証がキャンセルされました。',
    oauthErrors: {
      access_denied: 'Meta認証がキャンセルされました。',
      missing_parameters: '認証パラメータが不足しています。',
      invalid_state: '認証セッションが無効または期限切れです。もう一度お試しください。',
      state_mismatch: '認証データの不整合が発生しました。',
      callback_failed: 'Meta認証に失敗しました。もう一度お試しください。',
      product_unresolved: '対象プロダクトが特定できません。',
      product_not_found: 'プロダクトが見つかりません。',
      save_failed: 'Meta Ads接続の保存に失敗しました。',
      org_not_found: '組織情報が取得できませんでした。',
    },
    connectSuccess: '広告アカウントを接続しました。',
    writeEnabled: '作成・編集・停止が可能',
    writeDisabled: '閲覧のみ（書き込み不可）',
    reauthTitle: '書き込みには再認可が必要です',
    reauthBody:
      'この接続には ads_management が付与されていません。広告の作成・編集・停止を行うには、ads_read と ads_management を許可して再度連携してください。',
    reauthCta: '再認可する',
    lastChecked: '権限の最終確認',
    testAccount: 'テスト広告アカウント',
    authMethod: '接続方法',
    authMethodOAuth: 'OAuth（顧客セルフ連携）',
    authMethodToken: 'System User Token（手入力）',
    advancedTokenToggleShow: 'System User Token で接続する（管理者向け）',
    advancedTokenToggleHide: '閉じる',
    addAccount: '広告アカウントを追加',
    disconnectAll: 'すべて切断する',
    disconnectOne: '解除',
    confirmDisconnectOne: 'この広告アカウントの連携を解除しますか？',
    disconnectOneSuccess: '広告アカウントの連携を解除しました。',
    connectedBy: '接続ユーザー',
  },
  en: {
    pageTitle: 'Media Connections',
    loading: 'Loading...',
    providerName: 'Meta Ads',
    providerMetaSuffix: ' · ads_read + ads_management',
    connected: 'Connected',
    notConnected: 'Not connected',
    account: 'Connected account',
    adAccounts: 'Ad accounts',
    permissions: 'Granted permissions',
    openDashboard: 'Open dashboard',
    disconnect: 'Disconnect',
    processing: 'Processing...',
    connect: 'Connect',
    connecting: 'Connecting...',
    tokenLabel: 'System User Token',
    tokenPlaceholder: 'EAAB... (paste the System User Token issued in Business Manager)',
    helpTitle: 'About the System User Token',
    helpBody:
      'An admin-only alternative; most users should use "Connect Meta Ads" (OAuth) above. Paste the token issued under Business Settings > System Users in Meta Business Manager and Tasky verifies it server-to-server. The token is stored encrypted. ads_read is enough for read-only use; ads_management is also required to create, edit or pause ads. On connect, the backend verifies the token via /me, /me/permissions and /me/adaccounts.',
    errEmpty: 'Please enter a System User Token.',
    errConnect: 'Connection failed. Please make sure the System User Token is valid and has not expired.',
    errDisconnect: 'Failed to disconnect.',
    success: 'Connected successfully.',
    oauthSectionTitle: 'Connect Meta Ads',
    oauthSectionBody:
      'Authorize with your own Meta account to read ad performance and, after you approve each change on a confirmation screen, to create, edit and pause ads. Tokens are stored encrypted and are never shown in the UI, logs or API responses.',
    oauthConnect: 'Connect Meta Ads',
    oauthRedirecting: 'Redirecting to Meta...',
    oauthScopesLabel: 'Permissions requested',
    oauthNoBusinessManagement: 'business_management is not requested (Tasky does not manage Business assets).',
    pickerTitle: 'Select the ad account to use',
    pickerBody: 'Only ad accounts the authorized Meta user can access are listed.',
    pickerConfirm: 'Connect selected accounts',
    pickerConfirming: 'Connecting...',
    pickerCancel: 'Cancel',
    pickerEmpty: 'No accessible ad accounts were found.',
    pickerNoSelection: 'Please select at least one ad account.',
    errOAuthStart: 'Could not start Meta authorization.',
    errOAuthCandidates: 'Authorization session not found. Please start the connection again.',
    errOAuthConfirm: 'Failed to connect the ad accounts.',
    oauthCancelled: 'Meta authorization was cancelled.',
    oauthErrors: {
      access_denied: 'Meta authorization was cancelled.',
      missing_parameters: 'Required authorization parameters are missing.',
      invalid_state: 'The authorization session is invalid or expired. Please try again.',
      state_mismatch: 'The authorization data did not match. Please start again.',
      callback_failed: 'Meta authorization failed. Please try again.',
      product_unresolved: 'The target product could not be identified.',
      product_not_found: 'The product was not found.',
      save_failed: 'Failed to save the Meta Ads connection.',
      org_not_found: 'Could not load the organization.',
    },
    connectSuccess: 'Ad accounts connected.',
    writeEnabled: 'Create / edit / pause enabled',
    writeDisabled: 'Read-only (writes disabled)',
    reauthTitle: 'Re-authorization required before writing',
    reauthBody:
      'This connection does not have ads_management. To create, edit or pause ads, connect again and grant both ads_read and ads_management.',
    reauthCta: 'Re-authorize',
    lastChecked: 'Permissions last verified',
    testAccount: 'Test ad account',
    authMethod: 'Connection method',
    authMethodOAuth: 'OAuth (customer self-serve)',
    authMethodToken: 'System User Token (manual)',
    advancedTokenToggleShow: 'Connect with a System User Token (admin)',
    advancedTokenToggleHide: 'Hide',
    addAccount: 'Add ad account',
    disconnectAll: 'Disconnect all',
    disconnectOne: 'Remove',
    confirmDisconnectOne: 'Remove this ad account connection?',
    disconnectOneSuccess: 'Ad account removed.',
    connectedBy: 'Connected by',
  },
};

export const MD_STRINGS: Dict<{
  pageTitle: string;
  /** スマホのトップバー用。ページ名だと「…」で切れるので、下の媒体タブと同じ名前にする */
  topbarTitle: string;
  loading: string;
  notConnectedTitle: string;
  notConnectedBody: string;
  goToConnect: string;
  dataSource: string;
  fetched: string;
  presetToday: string;
  preset7: string;
  preset30: string;
  presetLifetime: string;
  kpiImpressions: string;
  kpiClicks: string;
  kpiReach: string;
  kpiConversions: string;
  kpiSpend: string;
  kpiCtr: string;
  refresh: string;
  refreshing: string;
  campaignsSection: string;
  proposalsSection: string;
  thCampaign: string;
  thStatus: string;
  thImpressions: string;
  thClicks: string;
  thReach: string;
  thConversions: string;
  thCtr: string;
  thSpend: string;
  thCpc: string;
  statusActive: string;
  statusPaused: string;
  campaignsEmpty: string;
  proposalsEmpty: string;
  sevHigh: string;
  sevMedium: string;
  sevLow: string;
  target: string;
  errLoad: string;
}> = {
  ja: {
    pageTitle: 'Meta 広告ダッシュボード',
    topbarTitle: 'Meta広告',
    loading: 'Meta から実データを取得中...',
    notConnectedTitle: 'Meta アカウントが接続されていません',
    notConnectedBody: '「メディア連携設定」で System User Token を接続すると、実際のキャンペーンデータが表示されます。',
    goToConnect: 'メディア連携設定へ',
    dataSource: 'データソース:',
    fetched: '取得:',
    presetToday: '今日',
    preset7: '7日間',
    preset30: '30日間',
    presetLifetime: '全期間',
    kpiImpressions: '表示回数 (Impressions)',
    kpiClicks: 'クリック (Clicks)',
    kpiReach: 'リーチ (Reach)',
    kpiConversions: 'コンバージョン (Conversions)',
    kpiSpend: '消化金額 (Spend)',
    kpiCtr: 'CTR',
    refresh: '更新',
    refreshing: '更新中...',
    campaignsSection: 'Meta キャンペーン',
    proposalsSection: 'AI 最適化提案',
    thCampaign: 'キャンペーン名',
    thStatus: 'ステータス',
    thImpressions: '表示回数',
    thClicks: 'クリック',
    thReach: 'リーチ',
    thConversions: 'CV',
    thCtr: 'CTR',
    thSpend: '消化金額',
    thCpc: 'CPC',
    statusActive: '配信中',
    statusPaused: '停止中',
    campaignsEmpty:
      'この広告アカウント・期間にはキャンペーンデータがありません。期間を「全期間」に変更するか、キャンペーンが配信されているアカウントを選択してください。',
    proposalsEmpty: '提案を生成するためのデータが不足しています。',
    sevHigh: '優先度: 高',
    sevMedium: '優先度: 中',
    sevLow: '優先度: 低',
    target: '対象:',
    errLoad: 'データの取得に失敗しました。',
  },
  en: {
    pageTitle: 'Meta Ads Dashboard',
    topbarTitle: 'Meta Ads',
    loading: 'Fetching live data from Meta...',
    notConnectedTitle: 'No Meta account connected',
    notConnectedBody: 'Connect a System User Token in “Media Connections” to display your real campaign data.',
    goToConnect: 'Go to Media Connections',
    dataSource: 'Data source:',
    fetched: 'Fetched:',
    presetToday: 'Today',
    preset7: '7 days',
    preset30: '30 days',
    presetLifetime: 'Lifetime',
    kpiImpressions: 'Impressions',
    kpiClicks: 'Clicks',
    kpiReach: 'Reach',
    kpiConversions: 'Conversions',
    kpiSpend: 'Spend',
    kpiCtr: 'CTR',
    refresh: 'Refresh',
    refreshing: 'Refreshing...',
    campaignsSection: 'Meta Campaigns',
    proposalsSection: 'AI Optimization Suggestions',
    thCampaign: 'Campaign',
    thStatus: 'Status',
    thImpressions: 'Impressions',
    thClicks: 'Clicks',
    thReach: 'Reach',
    thConversions: 'Conv.',
    thCtr: 'CTR',
    thSpend: 'Spend',
    thCpc: 'CPC',
    statusActive: 'Active',
    statusPaused: 'Paused',
    campaignsEmpty:
      'No campaign data for this ad account / period. Switch the range to “Lifetime”, or select an account that has delivering campaigns.',
    proposalsEmpty: 'Not enough data to generate suggestions.',
    sevHigh: 'Priority: High',
    sevMedium: 'Priority: Medium',
    sevLow: 'Priority: Low',
    target: 'Target:',
    errLoad: 'Failed to fetch data.',
  },
};

export const SB_STRINGS: Dict<{
  adOps: string;
  adDashboard: string;
  taapDashboard: string;
  taapProposals: string;
  metaDashboard: string;
  googleDashboard: string;
  mediaConnect: string;
  newChat: string;
  chatV2: string;
  sectionAds: string;
  sectionMaterial: string;
  sectionPlanning: string;
  sectionOther: string;
  jevEvaluation: string;
  bannerAds: string;
  bannerAbTest: string;
  carouselAds: string;
  movieCreation: string;
  videoAds: string;
  mangaAds: string;
  lpCreation: string;
  lpImprove: string;
  musicCreation: string;
  songAds: string;
  ugcAds: string;
  recipeVideo: string;
  imageGeneration: string;
  agentList: string;
  productList: string;
  aiDocument: string;
  aiDocument2: string;
  aiDocument3: string;
  aiVideoDocument: string;
  news: string;
  chatHistory: string;
  favorites: string;
  past7days: string;
  past30days: string;
  untitledChat: string;
  collapseSidebar: string;
  /** カード未登録ロック誘導ダイアログ。`{name}` は機能名に置換する */
  cardLockTitle: string;
  cardLockDesc: string;
  cardLockCta: string;
  cardLockLater: string;
}> = {
  ja: {
    adOps: '広告運用',
    adDashboard: '広告運用',
    taapDashboard: '運用ダッシュボード',
    taapProposals: '提案・承認',
    metaDashboard: 'Meta ダッシュボード',
    googleDashboard: 'Google Ads ダッシュボード',
    mediaConnect: 'メディア連携設定',
    newChat: '新しいチャット',
    chatV2: 'チャットv2',
    sectionAds: '広告制作',
    sectionMaterial: '素材制作',
    sectionPlanning: 'プランニング',
    sectionOther: 'その他',
    jevEvaluation: 'Jev評価',
    bannerAds: 'バナー広告',
    bannerAbTest: 'ABテスト',
    carouselAds: 'カルーセル制作（β）',
    movieCreation: 'ムービー制作（β）',
    videoAds: '動画広告（β）',
    mangaAds: '漫画広告（β）',
    lpCreation: 'LP制作（β）',
    lpImprove: 'LP改善（β）',
    musicCreation: '音楽生成（β）',
    songAds: '歌広告（β）',
    ugcAds: 'UGC動画広告（β）',
    recipeVideo: 'スタイル動画（β）',
    imageGeneration: '画像/動画生成',
    agentList: 'アシスタント一覧',
    productList: '商品情報',
    aiDocument: 'AI資料',
    aiDocument2: 'AI資料2',
    aiDocument3: 'AI資料3',
    aiVideoDocument: 'AI動画資料',
    news: 'ニュース',
    chatHistory: 'チャット履歴',
    favorites: 'お気に入り',
    past7days: '過去7日間',
    past30days: '過去30日間',
    untitledChat: '無題のチャット',
    collapseSidebar: 'サイドバーを折り畳む',
    cardLockTitle: '{name}はカードを登録すると使えます',
    cardLockDesc: 'いまは請求されません。プラン選択でクレジット3,000ぶんも一緒に付きます。',
    cardLockCta: 'カードを登録して使う',
    cardLockLater: 'あとにする',
  },
  en: {
    adOps: 'Ad Operations',
    adDashboard: 'Ad Operations',
    taapDashboard: 'Operations Dashboard',
    taapProposals: 'Proposals',
    metaDashboard: 'Meta Dashboard',
    googleDashboard: 'Google Ads Dashboard',
    mediaConnect: 'Media Connections',
    newChat: 'New chat',
    chatV2: 'Chat v2',
    sectionAds: 'Ad Creation',
    sectionMaterial: 'Assets',
    sectionPlanning: 'Planning',
    sectionOther: 'Other',
    jevEvaluation: 'Jev Evaluation',
    bannerAds: 'Banner Ads',
    bannerAbTest: 'A/B Test',
    carouselAds: 'Carousel Ads (Beta)',
    movieCreation: 'Movie Studio (Beta)',
    videoAds: 'Video Ads (Beta)',
    mangaAds: 'Comic Ads (Beta)',
    lpCreation: 'Landing Pages (Beta)',
    lpImprove: 'LP Optimizer (Beta)',
    musicCreation: 'Music (Beta)',
    songAds: 'Song Ads (Beta)',
    ugcAds: 'UGC Video Ads (Beta)',
    recipeVideo: 'Style Videos (Beta)',
    imageGeneration: 'Image / Video Studio',
    agentList: 'AI Assistants',
    productList: 'Products & Services',
    aiDocument: 'AI Documents',
    aiDocument2: 'AI Documents 2',
    aiDocument3: 'AI Documents 3',
    aiVideoDocument: 'AI Video Doc',
    news: 'News',
    chatHistory: 'Chat History',
    favorites: 'Favorites',
    past7days: 'Previous 7 days',
    past30days: 'Previous 30 days',
    untitledChat: 'Untitled chat',
    collapseSidebar: 'Collapse sidebar',
    cardLockTitle: '{name} unlocks once you add a card',
    cardLockDesc: "You won't be charged now. Choosing a plan also includes 3,000 credits.",
    cardLockCta: 'Add a card to unlock',
    cardLockLater: 'Maybe later',
  },
};

export const UM_STRINGS: Dict<{
  organization: string;
  accountSettings: string;
  aiScore: string;
  logout: string;
}> = {
  ja: {
    organization: '組織管理',
    accountSettings: 'アカウント設定',
    aiScore: '活用スコア',
    logout: 'ログアウト',
  },
  en: {
    organization: 'Organization',
    accountSettings: 'Account Settings',
    aiScore: 'Usage Score',
    logout: 'Log out',
  },
};

export const AUTH_STRINGS: Dict<{
  tagline: string;
  email: string;
  password: string;
  forgotPasswordShort: string;
  forgotPasswordLink: string;
  login: string;
  loggingIn: string;
  or: string;
  googleLogin: string;
  magicLinkLogin: string;
  resetPassword: string;
  invalidCredentialHint: string;
  sending: string;
  errEmailRequired: string;
  errEmailInvalid: string;
  errPasswordRequired: string;
  errLoginFailed: string;
  errMagicLink: string;
  errPopupBlocked: string;
  errDifferentCredential: string;
  errGoogleLogin: string;
}> = {
  ja: {
    tagline: '広告クリエイティブAIアシスタント',
    email: 'メールアドレス',
    password: 'パスワード',
    forgotPasswordShort: 'パスワードを忘れた？',
    forgotPasswordLink: 'パスワードをお忘れですか？',
    login: 'ログイン',
    loggingIn: 'ログイン中...',
    or: 'または',
    googleLogin: 'Googleでログイン',
    magicLinkLogin: 'メールリンクでログイン',
    resetPassword: 'パスワードを再設定',
    invalidCredentialHint: 'パスワードを忘れた場合は再設定するか、メールに届くリンクでログインできます。',
    sending: '送信中...',
    errEmailRequired: 'メールアドレスは必須です',
    errEmailInvalid: '有効なメールアドレスを入力してください',
    errPasswordRequired: 'パスワードは必須です',
    errLoginFailed: 'ログインに失敗しました。メールアドレスとパスワードをご確認ください。',
    errMagicLink: 'メールリンクの送信に失敗しました。しばらく時間をおいて再度お試しください。',
    errPopupBlocked: 'ポップアップがブロックされました。ポップアップを許可してください。',
    errDifferentCredential: 'このメールアドレスは別の方法で登録されています。',
    errGoogleLogin: 'Googleログインに失敗しました。もう一度お試しください。',
  },
  en: {
    tagline: 'AI assistant for ad creative work',
    email: 'Email address',
    password: 'Password',
    forgotPasswordShort: 'Forgot password?',
    forgotPasswordLink: 'Forgot your password?',
    login: 'Log in',
    loggingIn: 'Logging in...',
    or: 'or',
    googleLogin: 'Continue with Google',
    magicLinkLogin: 'Log in with email link',
    resetPassword: 'Reset password',
    invalidCredentialHint: 'Forgot your password? Reset it, or log in with a link sent to your email.',
    sending: 'Sending...',
    errEmailRequired: 'Email address is required',
    errEmailInvalid: 'Please enter a valid email address',
    errPasswordRequired: 'Password is required',
    errLoginFailed: 'Login failed. Please check your email address and password.',
    errMagicLink: 'Could not send the email link. Please wait a moment and try again.',
    errPopupBlocked: 'The popup was blocked. Please allow popups and try again.',
    errDifferentCredential: 'This email address is registered with a different sign-in method.',
    errGoogleLogin: 'Google sign-in failed. Please try again.',
  },
};

export const INVITE_MEMBER_STRINGS: Dict<{ invite: string }> = {
  ja: { invite: 'メンバーを招待' },
  en: { invite: 'Invite members' },
};

export const ONBOARDING_STRINGS: Dict<{
  heading: string;
  description: string;
  credit: (amount: string) => string;
  steps: Array<{ title: string; description: string }>;
}> = {
  ja: {
    heading: 'はじめの3ステップ',
    description: '3つのタスクを完了させて、ボーナスクレジットを獲得しましょう',
    credit: (amount) => `${amount}クレジット`,
    steps: [
      { title: '商品を登録する', description: 'URLを入れるだけ。戦略をプランニングします。（約2分）' },
      { title: '自分でバナーを3枚作る', description: '登録した商品から、ボタンひとつでバナーを作ります。' },
      { title: 'プランを選ぶ', description: '全機能が使える。あとでもOK' },
    ],
  },
  en: {
    heading: 'Your first 3 steps',
    description: 'Complete these tasks to earn bonus credits',
    credit: (amount) => `${amount} credits`,
    steps: [
      { title: 'Add a product', description: 'Just enter a URL and Tasky will plan your strategy. (About 2 min.)' },
      { title: 'Create 3 banners', description: 'Create banners from your product with one click.' },
      { title: 'Choose a plan', description: 'Unlock every feature. You can also do this later.' },
    ],
  },
};

export const GOOGLE_ADS_STRINGS: Dict<{
  apiMeta: string;
  connected: string;
  notConnected: string;
  loading: string;
  testAccount: string;
  status: string;
  lastSync: string;
  disconnect: string;
  mccNotice: string;
  selectionExpires: string;
  remainingMinutes: (minutes: number) => string;
  expired: string;
  currencyUnset: string;
  test: string;
  connectSelected: string;
  privacyBody: string;
  privacyPolicy: string;
  resume: string;
  restart: string;
  reconnect: string;
  connectOAuth: string;
  dashboard: string;
  confirmDisconnect: string;
  disconnectConfirm: string;
  selectRequired: string;
  loadError: string;
  resumeError: string;
  oauthError: string;
  oauthStartError: string;
  saveError: string;
  disconnectError: string;
  connectedSuccess: string;
  savedSuccess: string;
  disconnectedSuccess: string;
  oauthErrors: Record<string, string>;
}> = {
  ja: {
    apiMeta: '・OAuth連携', connected: '接続済み', notConnected: '未接続', loading: '接続状態を確認しています...',
    testAccount: 'テストアカウント', status: '状態', lastSync: '最終同期', disconnect: '解除',
    mccNotice: 'MCCは管理用のため選択できません。', selectionExpires: '選択の有効期限',
    remainingMinutes: (minutes) => `残り${minutes}分`,
    expired: 'Google認証は完了しましたが、アカウント選択の有効期限が切れました。「最初からやり直す」からGoogle認証をやり直してください。',
    currencyUnset: '通貨未設定', test: 'テスト', connectSelected: '選択したアカウントを連携',
    privacyBody: 'Google広告を連携すると、選択したアカウントの広告情報と実績を取得し、同じTasky組織のメンバーが閲覧できます。広告への変更は組織リーダーが内容を確認して承認します。連携を解除すると認証情報と取得した実績データを削除し、Google側の許可も取り消します（変更の監査記録は保存方針に従って保持します）。 詳しくは',
    privacyPolicy: 'プライバシーポリシー', resume: '前回の認証から再開する', restart: '最初からやり直す',
    reconnect: '再認証・アカウント追加', connectOAuth: 'Google広告をOAuth連携', dashboard: 'Google Adsダッシュボード',
    confirmDisconnect: 'このGoogle広告連携を解除しますか？', disconnectConfirm: '解除する', selectRequired: '連携する広告アカウントを1つ以上選択してください。',
    loadError: 'Google広告の接続状態を取得できませんでした。', resumeError: '前回のGoogle認証を確認できませんでした。「前回の認証から再開する」で再試行してください。',
    oauthError: 'Google広告の認証に失敗しました。「最初からやり直す」からお試しください。', oauthStartError: 'Google OAuthを開始できませんでした。',
    saveError: 'Google広告アカウントを保存できませんでした。', disconnectError: 'Google広告の連携を解除できませんでした。',
    connectedSuccess: 'Google広告アカウントは連携済みです。', savedSuccess: 'Google広告アカウントを連携しました。', disconnectedSuccess: 'Google広告の連携を解除しました。',
    oauthErrors: {
      access_denied: 'Googleでの認可がキャンセルされました。必要な場合はもう一度連携してください。',
      invalid_state: 'Google認証の受付期限が切れたか、認証をやり直したため、この認証は利用できません。「最初からやり直す」からGoogle認証を開始してください。',
      processing: 'Google認証を処理しています。少し待って「前回の認証から再開する」を押してください。',
      no_accounts: 'アクセス可能なGoogle広告アカウントが見つかりませんでした。', authentication: 'Google認証に失敗しました。別のGoogleアカウントも確認してください。',
      permission: 'Google広告アカウントへの権限が不足しています。',
    },
  },
  en: {
    apiMeta: ' · OAuth connection', connected: 'Connected', notConnected: 'Not connected', loading: 'Checking connection...',
    testAccount: 'Test account', status: 'Status', lastSync: 'Last synced', disconnect: 'Disconnect',
    mccNotice: 'Manager accounts (MCCs) cannot be selected.', selectionExpires: 'Selection expires',
    remainingMinutes: (minutes) => `${minutes} min. remaining`, expired: 'The account selection has expired. Start the Google authorization again.',
    currencyUnset: 'Currency not set', test: 'Test', connectSelected: 'Connect selected accounts',
    privacyBody: 'Connecting Google Ads lets members of this Tasky organization view ad details and performance for the selected accounts. Organization leaders review and approve changes to ads. Disconnecting removes authorization data and retrieved performance data and revokes access with Google (change audit records are retained according to our retention policy). Learn more in our',
    privacyPolicy: 'Privacy Policy', resume: 'Resume previous authorization', restart: 'Start over',
    reconnect: 'Re-authorize / add accounts', connectOAuth: 'Connect Google Ads with OAuth', dashboard: 'Google Ads Dashboard',
    confirmDisconnect: 'Disconnect this Google Ads connection?', disconnectConfirm: 'Disconnect', selectRequired: 'Select at least one ad account to connect.',
    loadError: 'Could not load the Google Ads connection.', resumeError: 'Could not restore the previous Google authorization. Try resuming it again.',
    oauthError: 'Google Ads authorization failed. Please start over.', oauthStartError: 'Could not start Google OAuth.',
    saveError: 'Could not save the Google Ads accounts.', disconnectError: 'Could not disconnect Google Ads.',
    connectedSuccess: 'Google Ads accounts are already connected.', savedSuccess: 'Google Ads accounts connected.', disconnectedSuccess: 'Google Ads disconnected.',
    oauthErrors: {
      access_denied: 'Google authorization was cancelled. Connect again if needed.', invalid_state: 'This Google authorization is invalid or expired. Please start over.',
      processing: 'Google authorization is still processing. Wait a moment, then resume the previous authorization.', no_accounts: 'No accessible Google Ads accounts were found.',
      authentication: 'Google authorization failed. Check the Google account and try again.', permission: 'You do not have sufficient access to the Google Ads account.',
    },
  },
};

export const HOME_STRINGS: Dict<{
  home: string;
  greetingMorning: string;
  greetingAfternoon: string;
  greetingEvening: string;
  greetingSuffix: string;
  tagline: string;
  quickStart: string;
  qaProductTitle: string;
  qaProductDesc: string;
  qaBannerTitle: string;
  qaBannerDesc: string;
  qaAssetTitle: string;
  qaAssetDesc: string;
  qaAgentTitle: string;
  qaAgentDesc: string;
  recentWork: string;
  seeAll: string;
  projectName: string;
  updatedAt: string;
  noRecentWork: string;
  detail: string;
  typeChat: string;
  typeImage: string;
  typeVideo: string;
  typeManga: string;
  typeLp: string;
  typeMusic: string;
  now: string;
  minutesAgo: string;
  hoursAgo: string;
  daysAgo: string;
  news: string;
  noNews: string;
  creditBalance: string;
  addCredits: string;
  monthlyBanners: string;
  monthlyVideos: string;
  products: string;
  trialRemaining: (days: number) => string;
  defaultUser: string;
}> = {
  ja: {
    home: 'ホーム',
    greetingMorning: 'おはようございます',
    greetingAfternoon: 'こんにちは',
    greetingEvening: 'こんばんは',
    greetingSuffix: 'さん',
    tagline: '今日も広告制作を爆速で回しましょう。',
    quickStart: '今すぐ始める',
    qaProductTitle: '商品情報を登録する',
    qaProductDesc: '商品情報を登録してバナー制作の素材にする',
    qaBannerTitle: 'バナー広告を作る',
    qaBannerDesc: '登録済み商品から複数サイズを一括生成',
    qaAssetTitle: '広告素材を作成する',
    qaAssetDesc: '動画・静止画の広告素材をAIで自動生成',
    qaAgentTitle: 'AIエージェントに相談する',
    qaAgentDesc: '広告戦略・クリエイティブの改善をAIに相談',
    recentWork: '最近の作業',
    seeAll: 'すべて見る →',
    projectName: 'プロジェクト名',
    updatedAt: '更新日時',
    noRecentWork: 'まだ作業履歴がありません',
    detail: '詳細',
    typeChat: 'AIチャット',
    typeImage: '画像生成',
    typeVideo: '動画広告',
    typeManga: '漫画広告',
    typeLp: 'LP制作',
    typeMusic: '音楽生成',
    now: '今',
    minutesAgo: '分前',
    hoursAgo: '時間前',
    daysAgo: '日前',
    news: 'お知らせ',
    noNews: 'お知らせはまだありません',
    creditBalance: 'クレジット残高',
    addCredits: 'クレジットを追加購入',
    monthlyBanners: '今月のバナー',
    monthlyVideos: '今月の動画',
    products: '登録商品',
    trialRemaining: (days: number) => `トライアル残り ${days} 日`,
    defaultUser: 'ユーザー',
  },
  en: {
    home: 'Home',
    greetingMorning: 'Good morning',
    greetingAfternoon: 'Good afternoon',
    greetingEvening: 'Good evening',
    greetingSuffix: '',
    tagline: "Let's speed through today's ad production.",
    quickStart: 'Quick Start',
    qaProductTitle: 'Register product info',
    qaProductDesc: 'Register products as source material for banner creation',
    qaBannerTitle: 'Create banner ads',
    qaBannerDesc: 'Generate multiple sizes at once from registered products',
    qaAssetTitle: 'Create ad assets',
    qaAssetDesc: 'Auto-generate video and image ad assets with AI',
    qaAgentTitle: 'Consult an AI agent',
    qaAgentDesc: 'Ask AI to improve your ad strategy and creatives',
    recentWork: 'Recent Work',
    seeAll: 'See all →',
    projectName: 'Project',
    updatedAt: 'Updated',
    noRecentWork: 'No work history yet',
    detail: 'View',
    typeChat: 'AI Chat',
    typeImage: 'Image Generation',
    typeVideo: 'Video Ads',
    typeManga: 'Comic Ads',
    typeLp: 'Landing Page',
    typeMusic: 'Music',
    now: 'now',
    minutesAgo: 'm ago',
    hoursAgo: 'h ago',
    daysAgo: 'd ago',
    news: 'News',
    noNews: 'No announcements yet',
    creditBalance: 'Credit Balance',
    addCredits: 'Buy more credits',
    monthlyBanners: 'Banners this month',
    monthlyVideos: 'Videos this month',
    products: 'Products',
    trialRemaining: (days: number) => `Trial: ${days} days left`,
    defaultUser: 'User',
  },
};

/**
 * Meta write flow (ads_management): ad plan form, confirmation screen,
 * execution result and audit log.
 */
export const MW_STRINGS: Dict<{
  sectionTitle: string;
  sectionIntro: string;
  disabledNoWrite: string;
  disabledNotAllowlisted: string;
  formTitle: string;
  campaignName: string;
  objective: string;
  adSetName: string;
  dailyBudget: string;
  dailyBudgetHint: string;
  startTime: string;
  endTime: string;
  country: string;
  creativeName: string;
  pageId: string;
  link: string;
  primaryText: string;
  headline: string;
  adName: string;
  buildPreview: string;
  building: string;
  confirmTitle: string;
  confirmIntro: string;
  cAccount: string;
  cAccountId: string;
  cOperation: string;
  cTargets: string;
  cBefore: string;
  cAfter: string;
  cDailyBudget: string;
  cLifetimeBudget: string;
  cSchedule: string;
  cResultStatus: string;
  opBundleCreate: string;
  opUpdate: string;
  opPause: string;
  notSet: string;
  validate: string;
  validating: string;
  execute: string;
  executing: string;
  discard: string;
  resultTitle: string;
  resultSucceeded: string;
  resultPartial: string;
  resultFailed: string;
  createdObjects: string;
  notCreated: string;
  graphCalls: string;
  auditTitle: string;
  auditEmpty: string;
  auditRefresh: string;
  thWhen: string;
  thOperation: string;
  thAccount: string;
  thStatus: string;
  thObjects: string;
  thBy: string;
  noticePreviewSaved: string;
  noticeValidated: string;
  errPreview: string;
  errValidate: string;
  errExecute: string;
  errAudit: string;
  confirmDialog: (suffix: string) => string;
}> = {
  ja: {
    sectionTitle: '広告を作成する（PAUSED）',
    sectionIntro:
      '入力内容から変更案を作り、確認画面で承認したときだけ Meta へ反映します。作成されるオブジェクトはすべて PAUSED で、tasky から自動で ACTIVE にすることはありません。',
    disabledNoWrite:
      'この接続には ads_management が付与されていないため、広告の作成・編集はできません。メディア連携設定から再認可してください。',
    disabledNotAllowlisted:
      'この環境では、この広告アカウントへの書き込みが許可されていません。検証用として許可された広告アカウントでのみ実行できます。',
    formTitle: '広告案の入力',
    campaignName: 'キャンペーン名',
    objective: '目的（objective）',
    adSetName: '広告セット名',
    dailyBudget: '日予算',
    dailyBudgetHint: 'アカウント通貨の最小単位（JPY なら 1 = 1円）で指定します。',
    startTime: '配信開始',
    endTime: '配信終了',
    country: '配信国コード',
    creativeName: 'クリエイティブ名',
    pageId: 'Facebook ページ ID',
    link: 'リンク先 URL',
    primaryText: 'メインテキスト',
    headline: '見出し',
    adName: '広告名',
    buildPreview: '確認画面を作成',
    building: '作成中...',
    confirmTitle: '実行前の確認',
    confirmIntro: 'この時点では Meta へ何も送信していません。内容を確認し「実行」を押すと反映されます。',
    cAccount: 'Meta 広告アカウント名',
    cAccountId: '広告アカウント ID',
    cOperation: '操作種別',
    cTargets: '操作対象',
    cBefore: '変更前',
    cAfter: '変更後',
    cDailyBudget: '日予算',
    cLifetimeBudget: '総予算',
    cSchedule: '配信期間',
    cResultStatus: '作成後のステータス',
    opBundleCreate: 'Campaign / Ad Set / Ad Creative / Ad を作成',
    opUpdate: 'オブジェクトを編集',
    opPause: 'オブジェクトを停止（PAUSE）',
    notSet: '未設定',
    validate: '事前検証',
    validating: '検証中...',
    execute: '実行して Meta へ反映',
    executing: '実行中...',
    discard: '破棄する',
    resultTitle: '実行結果',
    resultSucceeded: 'すべて反映しました',
    resultPartial: '一部のみ反映しました',
    resultFailed: '反映できませんでした',
    createdObjects: '作成できたオブジェクト',
    notCreated: '作成されなかったオブジェクト',
    graphCalls: '呼び出した Graph API',
    auditTitle: '監査ログ',
    auditEmpty: '監査ログはまだありません。',
    auditRefresh: '再読み込み',
    thWhen: '日時',
    thOperation: '操作',
    thAccount: '広告アカウント',
    thStatus: '結果',
    thObjects: 'オブジェクト ID',
    thBy: '実行者',
    noticePreviewSaved: '変更案を保存しました。まだ Meta へは反映されていません。',
    noticeValidated: '事前検証に成功しました。',
    errPreview: '変更案を作成できませんでした。',
    errValidate: '事前検証に失敗しました。',
    errExecute: 'Meta への反映に失敗しました。',
    errAudit: '監査ログを取得できませんでした。',
    confirmDialog: (suffix: string) =>
      `広告アカウント ${suffix} に、PAUSED 状態で Campaign / Ad Set / Ad Creative / Ad を作成します。実行しますか？`,
  },
  en: {
    sectionTitle: 'Create ads (PAUSED)',
    sectionIntro:
      'Tasky builds a change proposal from this form and only sends it to Meta after you approve it on the confirmation screen. Every object is created PAUSED and Tasky never sets anything to ACTIVE.',
    disabledNoWrite:
      'This connection does not have ads_management, so ads cannot be created or edited. Re-authorize from Media Connections.',
    disabledNotAllowlisted:
      'Writes to this ad account are not permitted in this environment. Only ad accounts approved for verification can be used.',
    formTitle: 'Ad plan',
    campaignName: 'Campaign name',
    objective: 'Objective',
    adSetName: 'Ad set name',
    dailyBudget: 'Daily budget',
    dailyBudgetHint: 'In the account currency minor unit (for JPY, 1 = 1 yen).',
    startTime: 'Start',
    endTime: 'End',
    country: 'Target country code',
    creativeName: 'Creative name',
    pageId: 'Facebook Page ID',
    link: 'Destination URL',
    primaryText: 'Primary text',
    headline: 'Headline',
    adName: 'Ad name',
    buildPreview: 'Build confirmation',
    building: 'Building...',
    confirmTitle: 'Confirm before applying',
    confirmIntro: 'Nothing has been sent to Meta yet. Review the details and press Apply to write them.',
    cAccount: 'Meta ad account name',
    cAccountId: 'Ad account ID',
    cOperation: 'Operation',
    cTargets: 'Target objects',
    cBefore: 'Before',
    cAfter: 'After',
    cDailyBudget: 'Daily budget',
    cLifetimeBudget: 'Lifetime budget',
    cSchedule: 'Schedule',
    cResultStatus: 'Resulting status',
    opBundleCreate: 'Create Campaign / Ad Set / Ad Creative / Ad',
    opUpdate: 'Edit object',
    opPause: 'Pause object',
    notSet: 'Not set',
    validate: 'Validate',
    validating: 'Validating...',
    execute: 'Apply to Meta',
    executing: 'Applying...',
    discard: 'Discard',
    resultTitle: 'Result',
    resultSucceeded: 'All objects were applied',
    resultPartial: 'Only part of the run was applied',
    resultFailed: 'Nothing was applied',
    createdObjects: 'Objects created',
    notCreated: 'Objects not created',
    graphCalls: 'Graph API calls made',
    auditTitle: 'Audit log',
    auditEmpty: 'No audit entries yet.',
    auditRefresh: 'Reload',
    thWhen: 'When',
    thOperation: 'Operation',
    thAccount: 'Ad account',
    thStatus: 'Result',
    thObjects: 'Object IDs',
    thBy: 'Approved by',
    noticePreviewSaved: 'Change proposal saved. Nothing has been sent to Meta yet.',
    noticeValidated: 'Validation passed.',
    errPreview: 'Could not build the change proposal.',
    errValidate: 'Validation failed.',
    errExecute: 'Applying the change to Meta failed.',
    errAudit: 'Could not load the audit log.',
    confirmDialog: (suffix: string) =>
      `Create a Campaign, Ad Set, Ad Creative and Ad in ad account ${suffix}, all PAUSED. Apply now?`,
  },
};

/** Toggle target: returns the language to switch *to*. */
export function otherLang(lang: MetaLang): MetaLang {
  return lang === 'ja' ? 'en' : 'ja';
}

/** Label shown on the toggle button (shows the language you'd switch to). */
export function toggleLabel(lang: MetaLang): string {
  return lang === 'ja' ? 'EN' : 'JA';
}
